"use client"

import { useState, useRef, useEffect } from "react"
import { Checkbox } from "@/components/ui/checkbox"
import { ChevronDown, ChevronUp, X } from "lucide-react"
import { useTranslation } from "@/hooks/use-translation"
import { Input } from "@/components/ui/input";
import { createPortal } from "react-dom";

interface Option {
  value: string;
  label?: string;
  color?: string;
  key?: string;
  isClearOption?: boolean;
}

interface CustomDropdownFieldProps {
  options: Option[]
  selectedValues: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  useColorCode?: boolean
  multiSelect?: boolean
  className?: string
  showSearch?: boolean
  portalContainer?: HTMLElement | null
  allowClear?: boolean // Allow clearing selection back to null
}

export function CustomDropdownField({
  options,
  selectedValues,
  onChange,
  placeholder,
  useColorCode = false,
  multiSelect = true,
  className = '',
  showSearch = true,
  portalContainer = null,
  allowClear = false,
}: CustomDropdownFieldProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [search, setSearch] = useState("");
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number, position: 'fixed' | 'absolute' } | null>(null);
  const triggerRectRef = useRef<DOMRect | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideDropdown = dropdownRef.current && dropdownRef.current.contains(target);
      const insideMenu = menuRef.current && menuRef.current.contains(target);
      if (!insideDropdown && !insideMenu) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Update menu position when open
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      triggerRectRef.current = rect;
      if (portalContainer) {
        const parentRect = portalContainer.getBoundingClientRect();
        setMenuPos({
          top: rect.bottom - parentRect.top,
          left: rect.left - parentRect.left,
          width: rect.width,
          position: 'absolute',
        });
      } else {
        setMenuPos({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
          position: 'fixed',
        });
      }
    }
  }, [isOpen, portalContainer]);

  // After menu renders, clamp within viewport and flip upward if needed
  useEffect(() => {
    if (!isOpen || !menuRef.current || !menuPos) return;
    if (menuPos.position !== 'fixed') return; // clipping mainly matters for fixed

    const viewportPadding = 8;
    const menuEl = menuRef.current;
    const menuHeight = menuEl.offsetHeight;
    const viewportHeight = window.innerHeight;
    let top = menuPos.top;

    // If bottom overflows, try to place above the trigger
    const triggerRect = triggerRectRef.current;
    if (top + menuHeight > viewportHeight - viewportPadding && triggerRect) {
      const desiredTop = Math.max(
        viewportPadding,
        triggerRect.top - Math.min(menuHeight, viewportHeight - 2 * viewportPadding)
      );
      if (desiredTop !== top) {
        setMenuPos(prev => prev ? { ...prev, top: desiredTop } : prev);
      }
    } else if (top < viewportPadding) {
      // Guard against top overflow
      setMenuPos(prev => prev ? { ...prev, top: viewportPadding } : prev);
    }
  }, [isOpen, menuPos]);

  const toggleOption = (value: string) => {
    if (multiSelect) {
      if (selectedValues.includes(value)) {
        onChange(selectedValues.filter((v) => v !== value))
      } else {
        onChange([...selectedValues, value])
      }
    } else {
      // Handle clear option: empty string means clear the selection
      if (value === '' && allowClear) {
        onChange([])
      } else {
        onChange([value])
      }
      setTimeout(() => setIsOpen(false), 0)
    }
  }

  // Get the display text based on selection state
  const displayText = selectedValues.length === 0
    ? placeholder
    : selectedValues.length === 1
      ? (options.find((opt) => opt.value === selectedValues[0])?.label || selectedValues[0])
      : `${selectedValues.length} selected`

  // Append clear option if allowClear is enabled, not in multiSelect mode, and has a selected value
  const hasClearOption = allowClear && !multiSelect && selectedValues.length > 0;
  
  // Filter options by search first
  const filteredOptionsBase = !showSearch || search.trim().length === 0
    ? options
    : options.filter(opt =>
        (opt.label || "").toLowerCase().includes(search.toLowerCase()) ||
        (opt.value || "").toLowerCase().includes(search.toLowerCase())
      );
  
  // Append clear option at the end after filtering
  const filteredOptions = hasClearOption
    ? [...filteredOptionsBase, { value: '', label: t('clear') || 'Clear', color: undefined, isClearOption: true }]
    : filteredOptionsBase;

  return (
    <div ref={dropdownRef}>
      <div
        className={`flex items-center justify-between px-3 py-2 text-sm cursor-pointer ${className || 'border border-input bg-background rounded-md'}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={!selectedValues.length ? "text-muted-foreground" : ""}>
          {displayText}
        </span>
        {isOpen ? <ChevronUp className="h-4 w-4 opacity-50" /> : <ChevronDown className="h-4 w-4 opacity-50" />}
      </div>

      {isOpen && menuPos && createPortal(
        <div
          className="z-[2147483647] rounded-md border bg-white shadow-lg"
          style={{
            position: menuPos.position,
            top: menuPos.top,
            left: menuPos.left,
            width: menuPos.width,
            minWidth: 200,
            maxHeight: '60vh',
            overflowY: 'auto',
          }}
          ref={menuRef}
          onClick={e => e.stopPropagation()}
        >
         {showSearch && (
           <div className="p-2 border-b border-gray-200 bg-white sticky top-0 z-10">
             <Input
               value={search}
               onChange={e => setSearch(e.target.value)}
               placeholder={t('search') || 'Search...'}
               className="w-full px-2 py-1 text-sm rounded border"
               autoFocus
             />
           </div>
         )}
          <div className="py-1">
            {filteredOptions.map((option) => (
              <div
                key={option.key || option.value || 'clear-option'}
                className={`flex items-center px-2 py-1.5 cursor-pointer ${
                  option.isClearOption 
                    ? 'hover:bg-red-50 text-red-600 border-t border-gray-200' 
                    : 'hover:bg-gray-100'
                }`}
                onClick={() => toggleOption(option.value)}
              >
                {multiSelect && !option.isClearOption && (
                  <Checkbox
                    id={`option-${option.value}`}
                    checked={selectedValues.includes(option.value)}
                    onCheckedChange={() => toggleOption(option.value)}
                    className="mr-2"
                  />
                )}
                <div className="flex items-center gap-2 flex-1">
                  {option.isClearOption ? (
                    <>
                      <X className="w-4 h-4" />
                      <span className="text-sm font-medium">{option.label || option.value}</span>
                    </>
                  ) : (
                    <>
                      {useColorCode && option.color && (
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: option.color }} />
                      )}
                      <span className="text-sm">{option.label || option.value}</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>,
        portalContainer || document.body
      )}
    </div>
  )
}
